import { PropsWithChildren, useCallback, useMemo } from "react";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { Cross1Icon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuIcon,
} from "@/components/ui/dropdown-menu";
import {
  FilterMenuItemOption,
  SingleFilterMenuItemType,
} from "../FilterMenu.model";
import {
  EqualFilter,
  isNullFilter,
  IsNullFilter,
  NotEqualFilter,
} from "../FieldFilter.model";
import styles from "../FilterMenu.module.scss";

interface SingleOptionsProps {
  options: FilterMenuItemOption[];
  selected?: string;
  onClick: (value: string) => void;
}

function SingleOptions({
  children,
  options,
  selected,
  onClick,
}: PropsWithChildren<SingleOptionsProps>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>

      <DropdownMenuContent align="start">
        {options.map(({ label, value, icon }) => (
          <DropdownMenuCheckboxItem
            key={value}
            checked={selected === value}
            onCheckedChange={() => onClick(value)}
          >
            {icon && <DropdownMenuIcon>{icon}</DropdownMenuIcon>}
            {label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface SingleActiveFilterItemProps<
  Field extends string,
> extends SingleFilterMenuItemType<Field> {
  value: EqualFilter | NotEqualFilter | IsNullFilter;
  size: "1" | "2" | "3";
  viewOnly: boolean;
  onChange: (item: SingleFilterMenuItemType<Field>) => void;
}

export function SingleActiveFilterItem<Field extends string>({
  size,
  viewOnly,
  disableOperator = false,
  onChange,
  ...item
}: SingleActiveFilterItemProps<Field>) {
  const selectedOption = useMemo(() => {
    return item.options.find(
      (option) => String(option.value) === String(item.value?.value),
    );
  }, [item]);

  const handleOperatorChange = useCallback(
    (operator: "eq" | "ne") => {
      if (isNullFilter(item.value)) {
        return;
      }

      onChange({
        ...item,
        value: {
          operator,
          value: item.value?.value,
        },
      });
    },
    [item, onChange],
  );

  const handleOptionClick = useCallback(
    (option: string) => {
      if (isNullFilter(item.value)) {
        onChange({
          ...item,
          value: {
            operator: "eq",
            value: option,
          },
        });
        return;
      }

      onChange({
        ...item,
        value: {
          operator: item.value?.operator ?? "eq",
          value: option,
        },
      });
    },
    [item, onChange],
  );

  const handleClear = useCallback(
    () => onChange({ ...item, value: undefined }),
    [item, onChange],
  );

  if (isNullFilter(item.value)) {
    return (
      <Flex gap={"1px"} className={styles.activeFilter} align="center">
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
        >
          {item.icon}
          {item.label}
        </div>

        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
        >
          is
        </div>

        <SingleOptions
          options={item.options}
          selected={item.value?.value}
          onClick={handleOptionClick}
        >
          <div
            data-accent-color="gray"
            className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
              viewOnly ? "pointer-events-none" : ""
            } shrink`}
          >
            Unknown
          </div>
        </SingleOptions>

        {!viewOnly && (
          <IconButton
            variant="soft"
            color="gray"
            size={size}
            onClick={handleClear}
          >
            <Cross1Icon />
          </IconButton>
        )}
      </Flex>
    );
  }

  if (selectedOption == null) return <></>;

  return (
    <Flex gap={"1px"} className={styles.activeFilter} align="center">
      <div
        data-accent-color="gray"
        className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
      >
        {item.icon}
        {item.label}
      </div>
      {disableOperator ? (
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
        >
          is
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div
              data-accent-color="gray"
              className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
                viewOnly ? "pointer-events-none" : ""
              }`}
            >
              {item.value?.operator === "eq" ? "is" : "is not"}
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start">
            <DropdownMenuCheckboxItem
              checked={item.value?.operator === "eq"}
              onCheckedChange={() => handleOperatorChange("eq")}
            >
              is
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={item.value?.operator === "ne"}
              onCheckedChange={() => handleOperatorChange("ne")}
            >
              is not
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <SingleOptions
        options={item.options}
        selected={selectedOption?.value}
        onClick={handleOptionClick}
      >
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
            viewOnly ? "pointer-events-none" : ""
          } shrink`}
        >
          <Flex align="center" gap="2">
            {selectedOption.icon}

            <Text truncate>{selectedOption.label}</Text>
          </Flex>
        </div>
      </SingleOptions>

      {!viewOnly && (
        <IconButton
          variant="soft"
          color="gray"
          size={size}
          onClick={handleClear}
        >
          <Cross1Icon />
        </IconButton>
      )}
    </Flex>
  );
}
